import { UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import { EntitySetName, EntityTypeName } from "@odata/GeneratedEntityTypes";
import { DocumentTypeCode } from "@odata/GeneratedEnums";
import { addBusinessPartnerType, isEmployeeBPType } from "@pages/documents/otherLiability/OtherLiability.utils";
import { addGrantClaimsTableToDocumentDef, addGrantTableToDocumentDef, shouldUseGrant } from "@pages/grant/Grant.utils";
import { isCashBasisAccountingCompany } from "@utils/CompanyUtils";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { Model } from "../../../model/Model";
import { AssetTranslations } from "../../asset/fixedAsset/FixedAsset.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition } from "../../PageUtils";
import { commonDocumentTranslations } from "../Document.utils";
import { addCorrectiveDocument, setItemsActionsVisibility } from "../DocumentCommonDefs";
import { clearEmptyDateGroupDateFields, getDefinitions as getDocumentDefinitions } from "../DocumentDef";
import { addItemsSummaryDef } from "../extensions/itemsSummary/ItemsSummary.utils";
import { enhanceIssuedDocDefinition } from "../invoicesIssued/InvoicesIssuedDef";
import OtherReceivableFormView from "./OtherReceivableFormView";


export const getDefinitions: IGetDefinition = (context: IAppContext): IDefinition => {
    const hasAccountAssignment = !isCashBasisAccountingCompany(context);

    const definition = cloneDeep(getDocumentDefinitions({
        entitySet: EntitySetName.OtherReceivables,
        documentType: DocumentTypeCode.OtherReceivable,
        tableId: `${EntityTypeName.OtherReceivable}Table`,
        formId: `${EntityTypeName.OtherReceivable}Form`,
        formControl: OtherReceivableFormView,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) => {
            const suffix = isEmployeeBPType(storage.data.entity.BusinessPartner) ? "Employee" : "";
            return getItemBreadCrumbsText(storage, i18next.t(`OtherReceivables:Breadcrumbs.NewReceivable${suffix}`),
                storage.data.entity?.NumberOurs && i18next.t(`OtherReceivables:Breadcrumbs.ReceivableWithNumber${suffix}`, { number: storage.data.entity.NumberOurs }));
        },
        permissions: [UserPermissionCodes.InvoicesReceived],
        context
    }));

    enhanceIssuedDocDefinition(definition, {
        docType: DocumentTypeCode.OtherReceivable,
        transFile: "OtherReceivables",
        isBusinessPartnerOptional: true,
        hasAccountAssignment,
        context
    });

    addCorrectiveDocument(definition, hasAccountAssignment);
    addItemsSummaryDef(definition, context);

    if (shouldUseGrant(context)) {
        addGrantTableToDocumentDef(definition, context);
        addGrantClaimsTableToDocumentDef(definition, context);
    }

    addBusinessPartnerType(definition);
    setItemsActionsVisibility(definition);

    return clearEmptyDateGroupDateFields(definition);
};

getDefinitions.translationFiles = ["OtherReceivables", ...AssetTranslations, ...commonDocumentTranslations];
setDefByEntityType(EntityTypeName.OtherReceivable, getDefinitions);